import { API_URLS } from '@helpers/api';
import { getSystemIcon, SystemIcon } from '@helpers/icons';
import { withQueryParams } from '@hoc/withQueryParams';
import { useApi } from '@hooks/useApi';
import Button from '@stories/Components/Buttons/Button/Button';
import ProductCard from '@stories/Components/Cards/ProductCard/ProductCard';
import MortgagesProductFilters from '@stories/Components/Listings/MortgageProductFilters/MortgageProductFilters';
import SortByDropdown from '@stories/Components/Listings/SortByDropdown/SortByDropdown';
import LoadingIndicator from '@stories/Components/Misc/LoadingIndicator/LoadingIndicator';
import { ContentType } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import isEqual from 'lodash/isEqual';
import qs from 'query-string';
import * as React from 'react';
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault, } from 'use-query-params';
import S from './ProductListingMortgages.styles';
const ProductListingMortgages = (props) => {
    const containerRef = React.useRef(null);
    const itemsRef = React.useRef([]);
    const [trayOpen, setTrayOpen] = React.useState(false);
    const [params, setParams] = useQueryParams(Object.assign({
        pageIndex: withDefault(NumberParam, 1),
        pageSize: withDefault(NumberParam, 6),
        sortBy: withDefault(StringParam, ''),
        purchasePrice: withDefault(StringParam, ''),
        depositAmount: withDefault(StringParam, ''),
        rateType: withDefault(ArrayParam, []),
    }));
    const api = useApi({
        initialData: props.initialData,
        url: `${API_URLS.mortgagesListing}?${qs.stringify({
            ...params,
            docId: props.pageContext?.documentId,
        })}`,
    });
    const [cards, setCards] = React.useState(props.initialData.items);
    const [paramsCache, setParamsCache] = React.useState(undefined);
    React.useEffect(() => {
        if (api.data && api.status === 'complete') {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { pageIndex, ...rest } = params;
            // If the params (minus pageIndex) haven't changed, append the new results to the existing ones
            if (isEqual(rest, paramsCache)) {
                setCards([...cards, ...api.data.items]);
            }
            else {
                setCards(api.data.items);
                setParamsCache(rest);
            }
        }
    }, [api.status]);
    // Cache params (minus page index) to ensure we don't render old results
    React.useEffect(() => {
        if (paramsCache) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { pageIndex, ...rest } = params;
        setParamsCache(rest);
    }, [params]);
    return (React.createElement(S.Container, { ref: containerRef, translations: props.translations },
        React.createElement(Button, { buttonType: "default", "aria-haspopup": "true", "aria-controls": "mortgages-listing-tray", onClick: () => setTrayOpen(true), icon: SystemIcon.ChevronDown, "data-filter-btn": true }, "Filters"),
        !props.hideFilters && (React.createElement(React.Fragment, null,
            React.createElement(S.FilterTray, { "data-open": trayOpen, id: "mortgages-listing-tray" },
                React.createElement(S.FiltersInner, null,
                    props.backCta && (React.createElement(S.BackButton, { href: props.backCta.url, target: props.backCta.target },
                        React.createElement(S.ButtonIcon, null, getSystemIcon(SystemIcon.ArrowRight)),
                        props.backCta.text)),
                    React.createElement(MortgagesProductFilters, { cancelLabel: props.translations?.['listings.mortgages.filters.cancel'], submitLabel: props.translations?.['listings.mortgages.filters.submit'] ?? '', onCancel: () => setTrayOpen(false), onSubmit: (values) => {
                            setParams({ ...values, pageIndex: 1 }, 'pushIn');
                            setTrayOpen(false);
                            containerRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }, values: {
                            purchasePrice: params.purchasePrice,
                            depositAmount: params.depositAmount,
                            rateType: params.rateType,
                        }, translations: props.translations }))),
            React.createElement(S.FilterTrayBackdrop, { onClick: () => setTrayOpen(false), "data-visible": trayOpen, "aria-controls": "mortgages-listing-tray", "aria-label": props.translations?.['listings.mortgages.filters.cancel'] }),
            props.sortOptions && (React.createElement(SortByDropdown, { onChange: (value) => setParams({ sortBy: value, pageIndex: 1 }, 'pushIn'), options: props.sortOptions, translations: props.translations, type: ContentType.Default, value: params.sortBy })))),
        cards.length > 0 ? (React.createElement(React.Fragment, null,
            cards.map((item, i) => (React.createElement(ProductCard, { key: item.id, ref: (el) => (itemsRef.current[i] = el), ...item, type: ContentType.Default, translations: props.translations, zIndex: cards.length - i }))),
            api?.data && cards.length > 0 ? (React.createElement(React.Fragment, null, api.data?.totalItems > cards.length && (React.createElement(S.Pagination, null,
                React.createElement(Button, { buttonType: props.type && props.type === ContentType.Intermediaries
                        ? 'intermediaries'
                        : 'default', onClick: () => {
                        /**
                         * Focus on the last current card to ensure that keyboard users can continue navigating
                         * through the cards in the correct order
                         */
                        itemsRef.current?.[cards.length - 1].querySelector('a')?.focus();
                        setParams({ pageIndex: params.pageIndex + 1 }, 'pushIn');
                    }, icon: api.status === 'loading' ? SystemIcon.Loader : SystemIcon.ArrowDown }, props.translations?.['global.listings.loadmore']))))) : null)) : (React.createElement(S.NoResultsText, null, props.translations?.['global.listings.noresults'])),
        api.status === 'loading' && (React.createElement(LoadingIndicator, { label: props.translations?.['global.listings.loading'], layout: "fixed", type: ContentType.Default }))));
};
export default withQueryParams(ProductListingMortgages);
